import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

/**
 * Sentinel used to indicate "the param should be removed from the URL".
 * We do not write `undefined` to `router.query` directly because Next.js
 * Pages Router merges objects shallowly and would leave stale keys.
 */
const UNSET = Symbol('unset');

/**
 * Default sentinel value used when a field is at its "show everything" state.
 * For most string filters this is the empty string.
 */
const DEFAULT_SENTINEL = '';

/**
 * Normalize a raw query-string value (which can be `string | string[] | undefined`)
 * into a trimmed string suitable for a single-value filter field.
 */
function readQueryString(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0];
  return '';
}

/**
 * Strip any key whose value matches `null`, `undefined`, or empty string.
 * Next.js Pages Router persists keys whose value is `null`/`undefined` if you
 * pass them explicitly, so we only set keys that have meaningful values.
 */
function compactQueryObject(query) {
  return Object.entries(query).reduce((acc, [key, value]) => {
    if (value === null || value === undefined || value === '') return acc;
    acc[key] = value;
    return acc;
  }, {});
}

/**
 * Returns a Pages-Router-friendly URL filter store.
 *
 * - Each field is a key in `router.query` (e.g. `?q=brakes&category=freins`).
 * - Fields at their default value (typically `''`) are omitted from the URL.
 * - Updates are pushed with `shallow: true` so `getStaticProps` / `getServerSideProps`
 *   are not re-run and the page does not fully reload.
 * - Deep links hydrate state on first `router.isReady`.
 * - External navigation (e.g. browser back/forward, programmatic push) is
 *   reflected back into the state via a `router.routeChangeComplete` listener,
 *   so the UI never drifts from the URL.
 *
 * @param {Object} options
 * @param {Record<string, { defaultValue: string, parse?: (raw: string) => string, serialize?: (value: string) => string }>} options.fields
 *        Declarative description of each filter field.
 * @param {Object} [options.extraQuery] Other query params to preserve (e.g. `lang`, `page`).
 * @param {string[]} [options.resetKeys]
 *        Subset of `fields` keys that `resetAll` should clear. When omitted,
 *        every owned field is cleared (legacy behavior). Pass an explicit
 *        list to preserve "context" fields like `productType` while only
 *        clearing search/brand/page.
 * @returns {{
 *   values: Record<string, string>,
 *   setField: (key: string, value: string) => void,
 *   resetAll: () => void,
 *   isReady: boolean,
 * }}
 */
export default function useUrlSyncedFilters({ fields, extraQuery, resetKeys } = {}) {
  const router = useRouter();
  const fieldKeys = useMemo(() => Object.keys(fields || {}), [fields]);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const resetKeysRef = useRef(resetKeys);
  resetKeysRef.current = resetKeys;

  // Track whether the next `routeChangeComplete` was triggered by us, so we
  // don't echo the URL change back into state and cause an extra render.
  const isInternalUpdateRef = useRef(false);

  // ---- Read state from the URL ------------------------------------------------
  const values = useMemo(() => {
    if (!router.isReady) {
      // Before the router hydrates we have nothing meaningful to read; expose
      // the declared defaults so the first render is stable.
      return Object.fromEntries(fieldKeys.map((key) => [key, fields[key].defaultValue]));
    }
    return Object.fromEntries(
      fieldKeys.map((key) => {
        const { defaultValue, parse } = fields[key];
        const raw = readQueryString(router.query?.[key]);
        if (raw === '') return [key, defaultValue];
        return [key, parse ? parse(raw) : raw];
      }),
    );
  }, [fieldKeys, fields, router.isReady, router.query]);

  // ---- Write helpers ----------------------------------------------------------
  const setField = useCallback(
    (key, value) => {
      if (!fieldKeys.includes(key)) return;

      const { defaultValue, serialize } = fieldsRef.current[key];
      const serialized = serialize ? serialize(value) : value;
      const nextValue = serialized === defaultValue ? UNSET : serialized;

      // Start from the live router query (preserves any foreign keys and the
      // current values of every other owned field), then only override the
      // single key being changed. We intentionally do NOT delete the other
      // owned fields here — that would clobber state the user is relying on
      // (e.g. dropping `?productType=forklift` when they change `?brand=`).
      const nextQuery = { ...(extraQuery || {}), ...router.query };

      if (nextValue === UNSET) {
        delete nextQuery[key];
      } else {
        nextQuery[key] = String(nextValue);
      }

      isInternalUpdateRef.current = true;
      router.push(
        {
          pathname: router.pathname,
          query: compactQueryObject(nextQuery),
        },
        undefined,
        { shallow: true, scroll: false },
      );
    },
    [extraQuery, fieldKeys, router],
  );

  const resetAll = useCallback(() => {
    const nextQuery = { ...(extraQuery || {}) };
    // Determine which owned fields to clear. When `resetKeys` is provided,
    // only those fields are dropped; the rest stay in the URL (so e.g. the
    // products page can clear `q`/`brand`/`page` while keeping the active
    // `productType` category). When `resetKeys` is omitted, every owned
    // field is cleared (legacy behavior).
    const resetList = Array.isArray(resetKeysRef.current)
      ? resetKeysRef.current
      : fieldKeys;
    const keptOwnedKeys = fieldKeys.filter((k) => !resetList.includes(k));

    // Seed `nextQuery` with: any explicit `extraQuery` keys, every foreign
    // router key, and every owned field that `resetKeys` told us to keep.
    // (We re-derive the kept owned fields from `router.query` so the URL
    // reflects the user's current intent — e.g. the active productType.)
    Object.keys(router.query).forEach((k) => {
      if (fieldKeys.includes(k)) {
        if (keptOwnedKeys.includes(k) && !(k in nextQuery)) {
          nextQuery[k] = router.query[k];
        }
        return;
      }
      if (k in nextQuery) return;
      nextQuery[k] = router.query[k];
    });

    // Drop the reset-list keys.
    resetList.forEach((k) => {
      if (fieldKeys.includes(k)) delete nextQuery[k];
    });

    isInternalUpdateRef.current = true;
    router.push(
      {
        pathname: router.pathname,
        query: compactQueryObject(nextQuery),
      },
      undefined,
      { shallow: true, scroll: false },
    );
  }, [extraQuery, fieldKeys, router]);

  // After every navigation, if it was triggered externally (e.g. back button)
  // the values memo will already re-derive from the new `router.query`, so
  // no extra effect is needed. We only use this ref to suppress any future
  // duplicate-setter logic if added.
  useEffect(() => {
    const handler = () => {
      // External navigation has settled; clear the internal flag.
      isInternalUpdateRef.current = false;
    };
    router.events.on('routeChangeComplete', handler);
    return () => {
      router.events.off('routeChangeComplete', handler);
    };
  }, [router]);

  return {
    values,
    setField,
    resetAll,
    isReady: router.isReady,
  };
}

export { DEFAULT_SENTINEL };
